import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isAuthenticated, persistLoginSession, clearSessionStorage, getAccessToken, getRefreshToken } from '../utils/auth';
import { bootstrapSession } from '../utils/bootstrapSession';
import { getSession, subscribeSession } from '../utils/sessionStore';
import { logout as apiLogout, refreshSessionTokens } from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSessionState] = useState(() => getSession());
  const [status, setStatus] = useState(() => (isAuthenticated() ? 'loading' : 'anonymous'));

  useEffect(() => {
    return subscribeSession((next) => setSessionState({ ...next }));
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      if (!isAuthenticated()) {
        if (!cancelled) {
          setStatus('anonymous');
        }
        return;
      }
      setStatus('loading');
      try {
        if (!getAccessToken() && getRefreshToken()) {
          await refreshSessionTokens();
        }
        await bootstrapSession();
        if (!cancelled) setStatus('authenticated');
      } catch {
        clearSessionStorage();
        if (!cancelled) setStatus('anonymous');
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (data, email) => {
    persistLoginSession(data, email);
    await bootstrapSession({
      email,
      role: data.role,
      allowUserAppOnWeb: data.allowUserAppOnWeb,
      allowAccessWithoutPayment: data.allowAccessWithoutPayment,
      canAccessRobots: data.canAccessRobots,
      subscriptionAdmin: data.subscriptionAdmin,
    });
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    setStatus('anonymous');
    await apiLogout();
  }, []);

  const refreshSession = useCallback(async () => {
    if (!isAuthenticated()) {
      setStatus('anonymous');
      return null;
    }
    return bootstrapSession();
  }, []);

  const value = useMemo(
    () => ({
      session,
      status,
      isLoading: status === 'loading',
      isAuthenticated: status === 'authenticated',
      login,
      logout,
      refreshSession,
    }),
    [session, status, login, logout, refreshSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
